import React from "react";
import {
  Webhook,
  Clock,
  MousePointerClick,
  Brain,
  Headset,
  Globe,
  Database,
  Code2,
  GitBranch,
  Hourglass,
  PauseCircle,
  Radio,
  BellRing,
  UserCheck,
  ScrollText,
  Mail,
  Bell,
  Zap,
  Package,
  Shuffle,
} from "lucide-react";

const BrandSvg = ({ children, color, size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    role="img"
    style={{ display: "block" }}
  >
    {color ? <g fill={color}>{children}</g> : children}
  </svg>
);

const GmailIcon = ({ size = 16 }) => (
  <BrandSvg size={size}>
    <path
      fill="#4285F4"
      d="M1.636 21.002h3.819v-9.273L0 7.638v11.727c0 .906.732 1.637 1.636 1.637z"
    />
    <path
      fill="#34A853"
      d="M18.545 21.002h3.819c.904 0 1.636-.731 1.636-1.637V7.638l-5.455 4.091z"
    />
    <path
      fill="#FBBC04"
      d="M18.545 4.547v7.182L24 7.638V5.456c0-2.024-2.31-3.179-3.927-1.964z"
    />
    <path
      fill="#EA4335"
      d="M5.455 11.729V4.547L12 9.456l6.545-4.909v7.182L12 16.638z"
    />
    <path
      fill="#C5221F"
      d="M0 5.456v2.182l5.455 4.091V4.547L3.927 3.492C2.309 2.277 0 3.432 0 5.456z"
    />
  </BrandSvg>
);

const DriveIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 87.3 78"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    role="img"
    style={{ display: "block" }}
  >
    <path fill="#0066DA" d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z" />
    <path fill="#00AC47" d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L.4 52.15C-.4 53.55 0 55.1 0 56.65h27.5z" transform="translate(0,1.2)" />
    <path fill="#EA4335" d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l5.85 11.5z" />
    <path fill="#00832D" d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.4-4.5 1.2z" />
    <path fill="#2684FC" d="M59.8 53H27.5L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.4 4.5-1.2z" />
    <path fill="#FFBA00" d="M73.4 26.5L60.7 4.5c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" />
  </svg>
);

const SheetsIcon = ({ size = 16 }) => (
  <BrandSvg size={size}>
    <path fill="#0F9D58" d="M14.4 0H3.6A1.6 1.6 0 0 0 2 1.6v20.8c0 .884.716 1.6 1.6 1.6h16.8a1.6 1.6 0 0 0 1.6-1.6V7.2L14.4 0z" />
    <path fill="#57BB8A" d="M14.4 0v5.6c0 .884.716 1.6 1.6 1.6h6L14.4 0z" />
    <path fill="#F1F1F1" d="M6.4 11.2v8h11.2v-8H6.4zm4.8 6.4H8v-1.6h3.2v1.6zm0-2.4H8v-1.6h3.2v1.6zm4.8 2.4h-3.2v-1.6H16v1.6zm0-2.4h-3.2v-1.6H16v1.6z" />
  </BrandSvg>
);

const DocsIcon = ({ size = 16 }) => (
  <BrandSvg size={size}>
    <path fill="#4285F4" d="M14.4 0H3.6A1.6 1.6 0 0 0 2 1.6v20.8c0 .884.716 1.6 1.6 1.6h16.8a1.6 1.6 0 0 0 1.6-1.6V7.2L14.4 0z" />
    <path fill="#A1C2FA" d="M14.4 0v5.6c0 .884.716 1.6 1.6 1.6h6L14.4 0z" />
    <path fill="#F1F1F1" d="M6 11h12v1.4H6zm0 3h12v1.4H6zm0 3h12v1.4H6zm0 3h8v1.4H6z" />
  </BrandSvg>
);

const CalendarIcon = ({ size = 16 }) => (
  <BrandSvg size={size}>
    <path fill="#FFFFFF" d="M5 5h14v14H5z" />
    <path fill="#1A73E8" d="M19.5 3.5h-2v-2h-1.5v2h-8v-2H6.5v2h-2A1.5 1.5 0 0 0 3 5v15a1.5 1.5 0 0 0 1.5 1.5h15A1.5 1.5 0 0 0 21 20V5a1.5 1.5 0 0 0-1.5-1.5zM19 19H5V8h14v11z" />
    <text x="12" y="16" textAnchor="middle" fontSize="6" fontWeight="700" fill="#1A73E8" fontFamily="Arial, sans-serif">31</text>
  </BrandSvg>
);

const SlackIcon = ({ size = 16 }) => (
  <BrandSvg size={size}>
    <path fill="#E01E5A" d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" />
    <path fill="#36C5F0" d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" />
    <path fill="#2EB67D" d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" />
    <path fill="#ECB22E" d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </BrandSvg>
);

const DiscordIcon = ({ size = 16 }) => (
  <BrandSvg size={size} color="#5865F2">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </BrandSvg>
);

const TelegramIcon = ({ size = 16 }) => (
  <BrandSvg size={size} color="#26A5E4">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </BrandSvg>
);

const WhatsAppIcon = ({ size = 16 }) => (
  <BrandSvg size={size} color="#25D366">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
  </BrandSvg>
);

const InstagramIcon = ({ size = 16 }) => {
  // Unique gradient id per render size so multiple icons on the page don't
  // clash on the same <defs> id.
  const gradId = `igGradient-${size}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      role="img"
      style={{ display: "block" }}
    >
      <defs>
        <radialGradient id={gradId} cx="0.3" cy="1" r="1.1">
          <stop offset="0%" stopColor="#FED576" />
          <stop offset="25%" stopColor="#F47133" />
          <stop offset="50%" stopColor="#BC3081" />
          <stop offset="100%" stopColor="#4C63D2" />
        </radialGradient>
      </defs>
      <path
        fill={`url(#${gradId})`}
        d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zm0 3.51A6.33 6.33 0 1 0 12 18.33 6.33 6.33 0 0 0 12 5.67zm0 10.44A4.11 4.11 0 1 1 12 7.89a4.11 4.11 0 0 1 0 8.22zm6.58-10.69a1.48 1.48 0 1 1-2.96 0 1.48 1.48 0 0 1 2.96 0z"
      />
    </svg>
  );
};

const ShopifyIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 109 124"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    role="img"
    style={{ display: "block" }}
  >
    <path fill="#95BF46" d="M74.7 14.8c0 0-1.4 0.4-3.7 1.1c-0.4-1.3-1-2.8-1.7-4.4c-2.4-4.5-5.9-7-10.1-7c0,0,0,0-0.1,0c-0.1-0.2-0.3-0.3-0.4-0.5C56.9 1.7 54.5 0.8 51.6 0.9c-5.6 0.2-11.2 4.3-15.7 11.5c-3.2 5.1-5.6 11.5-6.3 16.4c-6.5 2-11 3.4-11.1 3.5c-3.3 1-3.4 1.1-3.8 4.2c-0.3 2.3-8.9 68.6-8.9 68.6l71.8 12.4l31.1-7.7C108.7 109.8 75 14.9 74.7 14.8z M56.5 19.3c-3.5 1.1-7.4 2.3-11.3 3.5c1.1-4.2 3.2-8.4 5.8-11.1c1-1 2.3-2.2 3.9-2.8C56.4 12.1 56.6 16.6 56.5 19.3z M51.7 4.6c1.3 0 2.4 0.3 3.3 0.8c-1.5 0.8-2.9 1.9-4.3 3.3c-3.5 3.7-6.1 9.5-7.2 15.1c-3.2 1-6.4 2-9.3 2.9C36.2 17.2 43.3 4.8 51.7 4.6z M58.7 58.9c-0.2-2.4-1.5-2.4-2.7-2.5c-0.6 0-1.1-0.1-1.5-0.1l-2.1-7.4c0 0 4.4-2 7.2-2c1.5 0 1.6 0.5 1.6 0.9c0 0.4-0.7 6.7-1 8.6 C60.1 56.6 60.1 56.7 58.7 58.9z M67.4 17c-2.4 0.7-5.1 1.6-7.7 2.4c0-3.7-0.4-8.9-2.1-13.4C62.8 6.7 66.2 12.6 67.4 17z" />
    <path fill="#5E8E3E" d="M71 15.9c-0.3-0.1-6.4-0.5-6.4-0.5s-4.3-4.2-4.7-4.7c-0.2-0.2-0.4-0.3-0.7-0.3l-5.4 110.4l31.1-7.7c0 0-9.6-65.1-9.6-65.5C75.1 47.2 71.3 16 71 15.9z" />
    <path fill="#FFFFFF" d="M59.7 35l-3.6 13.4c0 0-4-1.8-8.7-1.5c-6.9 0.4-7 4.7-6.9 5.8c0.4 5.9 15.9 7.2 16.8 21c0.7 10.9-5.8 18.3-15 18.9c-11 0.7-17.1-5.8-17.1-5.8l2.3-9.9c0 0 6.1 4.6 11 4.3c3.2-0.2 4.4-2.8 4.3-4.7c-0.5-7.7-13.1-7.2-13.9-19.8c-0.7-10.6 6.3-21.4 21.7-22.4C56.4 33.9 59.7 35 59.7 35z" />
  </svg>
);

const PythonIcon = ({ size = 16 }) => (
  <BrandSvg size={size}>
    <defs>
      <linearGradient id="pyBlue" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#387EB8" />
        <stop offset="100%" stopColor="#366994" />
      </linearGradient>
      <linearGradient id="pyYellow" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#FFE052" />
        <stop offset="100%" stopColor="#FFC331" />
      </linearGradient>
    </defs>
    <path fill="url(#pyBlue)" d="M11.91 0c-1.04 0-2.04.087-2.91.252-2.575.484-3.043 1.498-3.043 3.367v2.46h6.087v.77H3.566c-1.882 0-3.531 1.131-4.046 3.282-.595 2.466-.621 4.005 0 6.58.46 1.916 1.56 3.282 3.443 3.282h2.087v-2.95c0-2.135 1.847-4.018 4.046-4.018h6.078c1.8 0 3.235-1.482 3.235-3.288V3.62c0-1.752-1.479-3.067-3.235-3.36C14.038.087 12.95 0 11.91 0zM8.62 1.866c.628 0 1.139.518 1.139 1.156 0 .635-.51 1.149-1.139 1.149-.631 0-1.139-.514-1.139-1.149 0-.638.508-1.156 1.139-1.156z" transform="translate(.5 .5)" />
    <path fill="url(#pyYellow)" d="M19.043 6.85v2.866c0 2.227-1.888 4.103-4.046 4.103H8.92c-1.77 0-3.235 1.515-3.235 3.288v6.158c0 1.753 1.524 2.784 3.235 3.286 2.05.602 4.014.711 6.078 0 1.629-.471 3.235-1.42 3.235-3.286v-2.467H12.151v-.77h9.087c1.882 0 2.583-1.313 3.235-3.283.674-2.025.645-3.973 0-6.578-.464-1.875-1.351-3.283-3.235-3.283h-2.195zm-3.453 14.728c.631 0 1.139.512 1.139 1.149 0 .638-.508 1.156-1.139 1.156-.628 0-1.139-.518-1.139-1.156 0-.637.51-1.149 1.139-1.149z" transform="translate(.5 .5)" />
  </BrandSvg>
);

const BRAND_RENDERERS = {
  gmail: GmailIcon,
  drive: DriveIcon,
  sheets: SheetsIcon,
  docs: DocsIcon,
  calendar: CalendarIcon,
  slack: SlackIcon,
  discord: DiscordIcon,
  telegram: TelegramIcon,
  whatsapp: WhatsAppIcon,
  instagram: InstagramIcon,
  shopify: ShopifyIcon,
  python: PythonIcon,
};

const PREFIX_BRAND = [
  { prefix: "gmail_", brand: "gmail" },
  { prefix: "drive_", brand: "drive" },
  { prefix: "sheets_", brand: "sheets" },
  { prefix: "docs_", brand: "docs" },
  { prefix: "calendar_", brand: "calendar" },
  { prefix: "gcal_", brand: "calendar" },
  { prefix: "slack_", brand: "slack" },
  { prefix: "discord_", brand: "discord" },
  { prefix: "telegram_", brand: "telegram" },
  { prefix: "whatsapp_", brand: "whatsapp" },
  { prefix: "instagram_", brand: "instagram" },
  { prefix: "shopify_", brand: "shopify" },
];

const LUCIDE_MAP = {
  webhook:           { Cmp: Webhook,           color: "text-violet-300" },
  scheduled:         { Cmp: Clock,             color: "text-blue-300" },
  manual:            { Cmp: MousePointerClick, color: "text-emerald-300" },
  ai_agent:          { Cmp: Headset,           color: "text-violet-300" },
  llm_call:          { Cmp: Brain,             color: "text-violet-300" },
  api_call:          { Cmp: Globe,             color: "text-cyan-300" },
  http_request:      { Cmp: Globe,             color: "text-cyan-300" },
  database:          { Cmp: Database,          color: "text-amber-300" },
  database_query:    { Cmp: Database,          color: "text-amber-300" },
  code_execution:    { Cmp: Code2,             color: "text-yellow-300" },
  condition:         { Cmp: GitBranch,         color: "text-pink-300" },
  if_else:           { Cmp: GitBranch,         color: "text-pink-300" },
  wait_duration:     { Cmp: Hourglass,         color: "text-sky-300" },
  wait_condition:    { Cmp: PauseCircle,       color: "text-sky-300" },
  wait_webhook:      { Cmp: Radio,             color: "text-sky-300" },
  wait_event:        { Cmp: BellRing,          color: "text-sky-300" },
  wait_user_input:        { Cmp: UserCheck,   color: "text-sky-300" },
  wait_for_human_action:  { Cmp: UserCheck,   color: "text-orange-300" },
  execute_workflow:       { Cmp: Shuffle,     color: "text-indigo-300" },
  log:               { Cmp: ScrollText,        color: "text-gray-300" },
  email_send:        { Cmp: Mail,              color: "text-rose-300" },
  email:             { Cmp: Mail,              color: "text-rose-300" },
  notification:      { Cmp: Bell,              color: "text-rose-300" },
};

const TYPE_FALLBACK = {
  agent:     { Cmp: Headset,    color: "text-violet-300" },
  trigger:   { Cmp: Zap,        color: "text-amber-300" },
  condition: { Cmp: GitBranch,  color: "text-pink-300" },
  action:    { Cmp: Package,    color: "text-gray-300" },
};

export const StepIcon = ({ step, size = 16, colorOverride }) => {
  const stepType = (step?.data?.step_type || "").toLowerCase();
  const triggerType = (step?.data?.trigger_type || "").toLowerCase();
  const key = stepType || triggerType;

  const lucideStyle = { width: size, height: size };

  if (key) {
    for (const { prefix, brand } of PREFIX_BRAND) {
      if (key.startsWith(prefix)) {
        const BrandCmp = BRAND_RENDERERS[brand];
        return <BrandCmp size={size} />;
      }
    }
    if (key === "shopify_event" || key === "shopify") return <ShopifyIcon size={size} />;
    if (key === "whatsapp_inbound" || key === "whatsapp_notification") return <WhatsAppIcon size={size} />;
    if (key === "instagram" || key === "instagram_webhook" || key === "instagram_inbound") return <InstagramIcon size={size} />;
    if (key === "code_execution_python") return <PythonIcon size={size} />;

    const lucide = LUCIDE_MAP[key];
    if (lucide) {
      const { Cmp, color } = lucide;
      return <Cmp style={lucideStyle} className={colorOverride || color} strokeWidth={2} />;
    }
  }

  const fallback = TYPE_FALLBACK[step?.type];
  if (fallback) {
    const { Cmp, color } = fallback;
    return <Cmp style={lucideStyle} className={colorOverride || color} strokeWidth={2} />;
  }

  if (step?.icon && typeof step.icon === "string") {
    return <span className="leading-none" style={{ fontSize: size }}>{step.icon}</span>;
  }
  return <Package style={lucideStyle} className={colorOverride || "text-gray-400"} strokeWidth={2} />;
};

export const ServiceGroupIcon = ({ groupKey, size = 14 }) => {
  const BrandCmp = BRAND_RENDERERS[groupKey];
  if (BrandCmp) return <BrandCmp size={size} />;
  if (groupKey === "http") return <Globe className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2} />;
  return <Package className="w-3.5 h-3.5 text-gray-400" strokeWidth={2} />;
};

export default StepIcon;
